/* eslint @typescript-eslint/no-explicit-any: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */
import http from '../http';
import { mapAddressFromAdressevaelgerDto, mapAddressFromGoogleDto, mapAddressPredictionFromGoogleDto } from './helpers';
/**
 * Danish addresses come from Klimadatastyrelsen's Adressevaelger; everywhere else uses Google.
 *
 * DAWA (dawa.aws.dk) was shut down on 2026-10-01 and answers 410 Gone, which broke the address
 * step of order placement for every Danish branch. Adressevaelger is its official successor.
 *
 * Two things differ from DAWA and are worth knowing before changing this file:
 *
 * 1. DAWA's /adresser/autocomplete returned the full address inline, so one call did everything
 *    and the prediction carried __internal_address. Adressevaelger's search returns a title and
 *    an id only, so the address is fetched when the user picks a suggestion. Resolving every hit
 *    up front instead is what forced a small result cap, and that cap is what made the list look
 *    empty; one search per keystroke and one lookup per selection is both cheaper and complete.
 *
 * 2. A search returns four kinds of hit and all of them are offered. Only `adresse` is a finished
 *    address; `husnummer`, `vejnavn` and `navngivenvejpostnummer` narrow the search instead, the
 *    rule the official widget applies. Keeping only `adresse` discarded more than half of what the
 *    service matched, because a street is mostly house numbers - 57 of the 100 hits for
 *    "Kochsgade" - and selecting a house number is how its individual flats are reached.
 *
 * The token is a plain identifier rather than a credential: Klimadatastyrelsen's stated purpose
 * for it is to be able to contact users of the service, and their own widget renders it into the
 * page. It is shipped in the bundle like GOOGLE_MAPS_API_KEY. User management is not yet in
 * operation on their side, so the published demo token is in use until they issue ours.
 */
const ADRESSEVAELGER_BASE_URL = process.env.ADRESSEVAELGER_API_ENDPOINT || 'https://adressevaelger.dk';
const ADRESSEVAELGER_TOKEN = process.env.ADRESSEVAELGER_TOKEN;
// Deliberately without the Authorization header - this is a third party, and our bearer token
// has no business being sent to it.
const adressevaelgerClient = http.createClient(ADRESSEVAELGER_BASE_URL, false);
const $window = typeof window !== 'undefined' ? window : undefined;
const GOOGLE_API_SCRIPT_ID = 'google-maps-api';
// Initialize google places API
if ($window && !document.head.querySelector(`#${GOOGLE_API_SCRIPT_ID}`)) {
    const GOOGLE_MAPS_API_KEY = process.env.GOOGLE_MAPS_API_KEY;
    const script = document.createElement('script');
    script.async = true;
    script.id = GOOGLE_API_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    document.head.appendChild(script);
}
let hasWarnedAboutMissingToken = false;
function assertTokenConfigured() {
    if (ADRESSEVAELGER_TOKEN) {
        return true;
    }
    if (!hasWarnedAboutMissingToken) {
        hasWarnedAboutMissingToken = true;
        // Not thrown: without a token the user simply types the address by hand, which is a far
        // better outcome than an address field that explodes.
        console.warn('ADRESSEVAELGER_TOKEN is not set - Danish address autocomplete is disabled.');
    }
    return false;
}
export default class AddressApi {
    // Annotated because the two return shapes would otherwise infer as a union, which degrades
    // `data` to any wherever the hook consumes it
    static async getAdressevaelgerAddressPredictions({ query, }) {
        if (!assertTokenConfigured()) {
            return [];
        }
        const hits = await this.searchAdressevaelger({ tekst: query });
        // Everything the service matched is offered. Most hits for a street are `husnummer`, so
        // keeping only `adresse` threw away over half of them. Only `adresse` is a finished
        // address; the rest narrow the search, which is how the official widget behaves.
        return hits
            .filter(hit => hit.titel)
            .map(hit => {
            var _a;
            return ({
                // A bare `vejnavn` hit carries no id, so its own title stands in as one
                id: (_a = hit.id) !== null && _a !== void 0 ? _a : hit.titel,
                text: hit.titel,
                ...(hit.type === 'adresse' ? {} : { __internal_refine: true }),
            });
        });
    }
    static async searchAdressevaelger(params) {
        var _a;
        const response = await adressevaelgerClient.get('/adresser/soeg', {
            params: { ...params, token: ADRESSEVAELGER_TOKEN },
        });
        return ((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.fund) || [];
    }
    static async resolveAdressevaelgerAddress(id) {
        const response = await adressevaelgerClient.get(`/adresser/${encodeURIComponent(id)}`, { params: { token: ADRESSEVAELGER_TOKEN } });
        return mapAddressFromAdressevaelgerDto(response === null || response === void 0 ? void 0 : response.data);
    }
    static async getAdressevaelgerAddressFromPrediction({ prediction }) {
        var _a;
        if (!assertTokenConfigured()) {
            throw new Error('Danish address lookup is not configured.');
        }
        const address = (_a = prediction.__internal_address) !== null && _a !== void 0 ? _a : (await this.resolveAdressevaelgerAddress(prediction.id));
        if (!address) {
            throw new Error('Invalid prediction supplied to address api.');
        }
        return address;
    }
    static async getGoogleAddressPredictions({ query, countryCode, }) {
        const service = new google.maps.places.AutocompleteService();
        const data = await service.getPlacePredictions({
            input: query,
            componentRestrictions: {
                country: countryCode,
            },
            types: ['address'],
        });
        return data.predictions.map(mapAddressPredictionFromGoogleDto).filter(Boolean);
    }
    static async getGoogleAddressFromPrediction({ prediction: { id } }) {
        const service = new google.maps.Geocoder();
        const data = await service.geocode({ placeId: id });
        const address = mapAddressFromGoogleDto(data.results[0]);
        if (!address) {
            throw new Error('Invalid prediction supplied to address api.');
        }
        return address;
    }
    static providerSupportsSubpremise(countryCode) {
        // This dictates whether or not to show subpremise fields in the UI alongside the address autocomplete
        // true: hide additional fields
        // false: show additional fields
        return countryCode === 'dk';
    }
    static providerUsesDanishAddressApi(countryCode) {
        return countryCode === 'dk'; // Adressevaelger only provides DK addresses
    }
    static async getAddressFromPrediction(params) {
        return this.providerUsesDanishAddressApi(params.countryCode)
            ? this.getAdressevaelgerAddressFromPrediction(params)
            : this.getGoogleAddressFromPrediction(params);
    }
    static async getAddressPredictions(params) {
        return this.providerUsesDanishAddressApi(params.countryCode)
            ? this.getAdressevaelgerAddressPredictions(params)
            : this.getGoogleAddressPredictions(params);
    }
}
